import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, ChevronRight, Search, X } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import { BLOG_POSTS } from "./posts";
import { getCover } from "./covers";

function PostCard({ post }) {
  const cover = getCover(post.category);
  const imgSrc = `/blog-images/${post.slug}.jpg`;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group bg-white rounded-3xl overflow-hidden border border-[#2c4a3b]/8 hover:border-[#bc6c25]/40 hover:shadow-[0_20px_40px_-15px_rgba(26,36,33,0.18)] hover:-translate-y-1 transition-all duration-500"
    >
      <div
        className="relative aspect-[4/3] overflow-hidden"
        style={{ background: cover.bg }}
      >
        <img
          src={imgSrc}
          alt={post.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] font-bold text-white drop-shadow">
          {post.category}
        </div>
        <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.18em] italic text-white/85">
          {cover.label}
        </div>
      </div>

      <div className="p-6 md:p-7">
        <h2 className="font-serif text-xl md:text-2xl leading-tight text-[#1a2421] group-hover:text-[#bc6c25] transition-colors">
          {post.title}
        </h2>
        <p className="mt-3 text-sm text-[#5c6b61] leading-relaxed line-clamp-3">{post.excerpt}</p>
        <div className="mt-5 flex items-center justify-between text-xs text-[#5c6b61]">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.minutes} min · {(post.date || "").slice(0, 10)}
          </span>
          <span className="inline-flex items-center gap-1 text-[#bc6c25] font-semibold uppercase tracking-[0.18em]">
            Leer <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogHub({ t, language, setLanguage }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeCat, setActiveCat] = useState("Todos");
  const [query, setQuery] = useState("");

  const categories = useMemo(() => ["Todos", ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))], []);

  const sortedPosts = useMemo(() => {
    return [...BLOG_POSTS].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = sortedPosts;
    if (activeCat !== "Todos") {
      result = result.filter((p) => p.category === activeCat);
    }
    if (q.length >= 2) {
      result = result.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.slug.includes(q)
      );
    }
    return result;
  }, [activeCat, sortedPosts, query]);

  return (
    <main className="relative bg-[#f9f6f0] text-[#1a2421]" data-testid="blog-hub">
      <Helmet>
        <title>Blog Eliora — Biohacking, glutatión, Immunotec y medicina funcional</title>
        <meta name="description" content="Artículos sobre Immunocal, glutatión, sistema inmunológico, biohacking, medicina funcional y suplementos Immunotec. Aprende a optimizar tu salud con ciencia." />
        <link rel="canonical" href="https://elioralongevity.com/blog" />
        <meta property="og:title" content="Blog Eliora — Biohacking, glutatión, Immunotec y medicina funcional" />
        <meta property="og:description" content="Aprende de Immunocal, glutatión y medicina funcional. Contenido respaldado por ciencia." />
        <meta property="og:url" content="https://elioralongevity.com/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog Eliora Longevity",
          url: "https://elioralongevity.com/blog",
          publisher: { "@type": "Organization", name: "Eliora Longevity" },
          blogPost: BLOG_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://elioralongevity.com/blog/${p.slug}`,
            datePublished: p.date,
            description: p.excerpt,
          })),
        })}</script>
      </Helmet>

      <Header t={t} language={language} setLanguage={setLanguage} />

      {/* Hero */}
      <section className="border-b border-[#2c4a3b]/8 pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Blog Eliora</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Ciencia, biohacking y <em className="text-[#bc6c25]">medicina funcional</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#5c6b61] leading-relaxed">
            Todos los artículos para entender cómo cuidar tu cuerpo desde la raíz. Filtra por categoría, busca por tema, o explora los más nuevos primero.
          </p>

          {/* Search bar */}
          <div className="mt-8 max-w-xl relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c6b61]" />
            <input
              type="text"
              data-testid="blog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar: diabetes, menopausia, immunocal, glutatión..."
              className="w-full pl-12 pr-12 py-3.5 rounded-full bg-white border border-[#2c4a3b]/15 focus:border-[#bc6c25] focus:outline-none focus:ring-2 focus:ring-[#bc6c25]/20 text-sm text-[#1a2421] placeholder-[#5c6b61]/60 shadow-sm"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="blog-search-clear"
                aria-label="Limpiar búsqueda"
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-[#f0ebe1] text-[#5c6b61]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Categories filter */}
      <section className="sticky top-16 md:top-20 z-30 bg-[#f9f6f0]/90 backdrop-blur-xl border-b border-[#2c4a3b]/8 py-4">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-wrap gap-2 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`filter-${c}`}
              className={`text-xs uppercase tracking-[0.18em] rounded-full px-4 py-2 transition-all whitespace-nowrap ${
                activeCat === c
                  ? "bg-[#bc6c25] text-white font-semibold"
                  : "bg-white border border-[#2c4a3b]/10 text-[#5c6b61] hover:border-[#bc6c25]/30 hover:text-[#1a2421]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Posts gallery */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-xs text-[#5c6b61] mb-8">
            {query.trim().length >= 2 ? (
              <>Resultados para "<strong className="text-[#1a2421]">{query}</strong>": <strong className="text-[#1a2421]">{filtered.length}</strong> {filtered.length === 1 ? "artículo" : "artículos"}</>
            ) : (
              <>Mostrando <strong className="text-[#1a2421]">{filtered.length}</strong> de {BLOG_POSTS.length} artículos · ordenados del más nuevo al más antiguo</>
            )}
          </p>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-6">
              <p className="font-serif text-2xl text-[#1a2421]">No encontramos artículos con esa búsqueda.</p>
              <p className="text-sm text-[#5c6b61] mt-3 max-w-md mx-auto">
                Intenta con términos más simples como "diabetes", "menopausia", "glutatión", "Immunocal" o "frutos rojos".
              </p>
              <button
                onClick={() => { setQuery(""); setActiveCat("Todos"); }}
                className="mt-6 px-6 py-2.5 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white text-sm font-semibold"
                data-testid="blog-reset-filters"
              >
                Ver todos los artículos
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {filtered.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer t={t} />
      <WhatsAppButton label={t?.whatsapp} />
    </main>
  );
}
